import { getEquipmentQueryOptions } from "@packages/api-client/hooks/equipmentController";
import type { UpdateEquipmentMutationRequest } from "@packages/api-client/types/equipmentController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useUpdateEquipment } from "@/api/equipment";
import { PageHeader } from "@/components/PageHeader";
import { FormSkeleton } from "@/components/ui/skeleton";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { EquipmentForm } from "../-components/EquipmentForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/fleet/$equipmentId/edit",
)({
	// Roadmap rule 2: edits never queue offline (conflict-prone, low urgency).
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	component: EditEquipmentPage,
});

function EditEquipmentPage() {
	const { orgId, equipmentId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const { data: equipment, isLoading } = useQuery(
		getEquipmentQueryOptions(orgId, equipmentId),
	);

	const updateMutation = useUpdateEquipment();

	const handleSubmit = (formData: UpdateEquipmentMutationRequest) =>
		updateMutation
			.mutateAsync({ organizationId: orgId, equipmentId, data: formData })
			.then(() => {
				navigate({
					to: "/app/org/$orgId/equipment/fleet/$equipmentId",
					params: { orgId, equipmentId },
				});
			});

	if (isLoading) {
		return <FormSkeleton />;
	}

	if (!equipment) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">{t("equipment.fleet.notFound")}</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-6 h-full">
			<PageHeader title={t("equipment.fleet.edit.title")} showBackButton />
			<EquipmentForm
				equipment={equipment}
				orgId={orgId}
				onSubmit={handleSubmit}
				isSubmitting={updateMutation.isPending}
				onCancel={() =>
					navigate({
						to: "/app/org/$orgId/equipment/fleet/$equipmentId",
						params: { orgId, equipmentId },
					})
				}
			/>
		</div>
	);
}
